import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  limit,
} from "firebase/firestore";
import Nav from "../components/Nav";
import SEO, {
  generateOrganizationSchema,
  generateWebSiteSchema,
} from "../components/SEO";
import ArticleImage from "../components/ArticleImage";
import { db } from "../firebase";
import {
  getArticleCategoryUrl,
  getResourceCategoryUrl,
} from "../utils/seoPaths";
import {
  filterPublishedArticles,
  getPublicationDate,
} from "../utils/articlePublishing";

// Add this component after your imports
const SearchBar = () => {
  const [searchInput, setSearchInput] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      window.location.href = `/articles?search=${encodeURIComponent(
        searchInput.trim()
      )}`;
    } else {
      window.location.href = "/articles";
    }
  };

  return (
    <form onSubmit={handleSearch} className="flex">
      <input
        type="text"
        placeholder="Search articles..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        className="flex-1 border-2 border-gray-600 bg-gray-800 text-white px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none placeholder-gray-400"
      />
      <button
        type="submit"
        className="bg-cyan-500 text-gray-900 px-4 py-2 hover:bg-cyan-400 transition-colors"
      >
        <span className="text-sm font-semibold">GO</span>
      </button>
    </form>
  );
};

const FrontPage = () => {
  const [featuredArticle, setFeaturedArticle] = useState(null);
  const [unsungHeroes, setUnsungHeroes] = useState([]);
  const [recoveryStories, setRecoveryStories] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllContent();
  }, []);

  // ✅ Tell Prerender we’re ready (don’t wait for Firestore — OG tags don’t depend on it)
  useEffect(() => {
    const id = setTimeout(() => {
      if (typeof window !== "undefined") window.prerenderReady = true;
    }, 0);
    return () => clearTimeout(id);
  }, []);

  const loadAllContent = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadFeaturedArticle(),
        loadUnsungHeroes(),
        loadRecoveryStories(),
        loadRecentArticles(),
        loadBlogPosts(),
      ]);
    } catch (error) {
      console.error("Error loading content:", error);
    }
    setLoading(false);
  };

  const loadFeaturedArticle = async () => {
    try {
      const q = query(
        collection(db, "articles"),
        where("featured", "==", true),
        where("date", "<=", getPublicationDate()),
        orderBy("date", "desc"),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      const doc = querySnapshot.docs.find((item) =>
        filterPublishedArticles([{ id: item.id, ...item.data() }]).length
      );
      if (doc) {
        setFeaturedArticle({
          id: doc.id,
          ...doc.data(),
        });
      }
    } catch (error) {
      console.error("Error loading featured article:", error);
    }
  };

  const loadUnsungHeroes = async () => {
    try {
      const q = query(
        collection(db, "articles"),
        where("category", "==", "unsung-heroes"),
        where("date", "<=", getPublicationDate()),
        orderBy("date", "desc"),
        limit(2)
      );
      const querySnapshot = await getDocs(q);
      let heroes = [];
      querySnapshot.forEach((doc) => {
        heroes.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      heroes = filterPublishedArticles(heroes).slice(0, 2);
      setUnsungHeroes(heroes);
    } catch (error) {
      console.error("Error loading unsung heroes:", error);
    }
  };

  const loadRecoveryStories = async () => {
    try {
      const q = query(
        collection(db, "articles"),
        where("category", "==", "stories-of-recovery"),
        where("date", "<=", getPublicationDate()),
        orderBy("date", "desc"),
        limit(2)
      );
      const querySnapshot = await getDocs(q);
      let stories = [];
      querySnapshot.forEach((doc) => {
        stories.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      stories = filterPublishedArticles(stories).slice(0, 2);
      setRecoveryStories(stories);
    } catch (error) {
      console.error("Error loading recovery stories:", error);
    }
  };

  const loadRecentArticles = async () => {
    try {
      const q = query(
        collection(db, "articles"),
        where("date", "<=", getPublicationDate()),
        orderBy("date", "desc"),
        limit(5)
      );
      const querySnapshot = await getDocs(q);
      const articles = [];
      querySnapshot.forEach((doc) => {
        articles.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setRecentArticles(filterPublishedArticles(articles).slice(0, 5));
    } catch (error) {
      console.error("Error loading recent articles:", error);
    }
  };

  const loadBlogPosts = async () => {
    try {
      const q = query(
        collection(db, "articles"),
        where("category", "==", "blog-posts"),
        where("date", "<=", getPublicationDate()),
        orderBy("date", "desc"),
        limit(3)
      );
      const querySnapshot = await getDocs(q);
      let posts = [];
      querySnapshot.forEach((doc) => {
        posts.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      posts = filterPublishedArticles(posts).slice(0, 3);
      setBlogPosts(posts);
    } catch (error) {
      console.error("Error loading blog posts:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-serif">
        <Nav />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold text-cyan-400 mb-4">
            Loading Content...
          </h1>
          <p className="text-gray-300">
            Please wait while we fetch the latest articles.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-serif">
      <SEO
        title="New Hampshire SUD & Mental Health Resources"
        description="Find New Hampshire substance use disorder (SUD) and mental health resources: treatment providers, recovery housing, peer support, crisis services, and transportation."
        canonicalUrl="https://www.recoverycompassnh.com/"
        ogType="website"
        ogImage="https://www.recoverycompassnh.com/og-default.png"
        ogImageAlt="Recovery Compass NH - New Hampshire's comprehensive recovery resource hub"
        schema={[generateOrganizationSchema(), generateWebSiteSchema()]}
      />
      <Nav />


      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content Area - Left Column */}
          <main className="lg:col-span-8 order-1">
            {/* Featured Story */}
            {featuredArticle && (
              <article className="mb-8 border-b-2 border-cyan-500 pb-6">
                <div className="flex items-center mb-2">
                  <span className="bg-cyan-500 text-gray-900 px-3 py-1 text-sm font-bold tracking-wider">
                    FEATURED STORY
                  </span>
                  <div className="ml-4 h-px bg-gray-600 flex-1"></div>
                </div>
                <Link to={`/articles/${featuredArticle.slug || featuredArticle.id}`}>
                  <h2 className="text-2xl md:text-4xl font-bold mb-4 leading-tight text-white hover:text-cyan-400 transition-colors cursor-pointer">
                    {featuredArticle.title}
                  </h2>
                </Link>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2 order-2 md:order-1">
                    <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-4">
                      {featuredArticle.excerpt}
                    </p>
                    <div className="mt-4 text-sm text-gray-400">
                      {featuredArticle.author && (
                        <>
                          <span className="font-semibold text-cyan-400">
                            By {featuredArticle.author}
                          </span>
                          <span className="mx-2">|</span>
                        </>
                      )}
                      <span>{formatDate(featuredArticle.date)}</span>
                    </div>
                  </div>
                  <div className="md:col-span-1 order-1 md:order-2">
                    {featuredArticle.imageUrl ? (
                      <img
                        src={featuredArticle.imageUrl}
                        alt={featuredArticle.title}
                        className="w-full h-48 object-cover border-2 border-gray-600 rounded"
                      />
                    ) : (
                      <div className="bg-gray-700 h-48 rounded border-2 border-gray-600 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">
                          Featured Image
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            )}

            {/* Two Main Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Unsung Heroes Section */}
              <section className="md:border-r border-gray-600 md:pr-6 flex flex-col border-b md:border-b-0 border-gray-600 pb-6 md:pb-0">
                <div className="border-b-2 border-cyan-500 mb-4 pb-2">
                  <h3 className="text-xl md:text-2xl font-bold text-cyan-400">
                    UNSUNG HEROES
                  </h3>
                  <p className="text-sm text-gray-400 italic">
                    The Dedicated Staff
                  </p>
                </div>

                {unsungHeroes.map((hero, index) => (
                  <article key={hero.id} className="mb-6">
                    <Link to={`/articles/${hero.slug || hero.id}`}>
                      <h4
                        className={`${
                          index === 0
                            ? "text-lg md:text-xl"
                            : "text-base md:text-lg"
                        } font-${
                          index === 0 ? "bold" : "semibold"
                        } mb-2 hover:text-cyan-400 cursor-pointer text-white`}
                      >
                        {hero.title}
                      </h4>
                    </Link>
                    {index === 0 && hero.imageUrl && (
                      <div className="w-full h-96 mb-3 border border-gray-600 rounded overflow-hidden">
                        <ArticleImage src={hero.imageUrl} alt={hero.title} />
                      </div>
                    )}

                    <p className="text-sm text-gray-300 leading-relaxed mb-2">
                      {hero.excerpt.substring(0, 150)}...
                    </p>
                    <span className="text-xs text-gray-500">
                      {hero.readTime}
                    </span>
                  </article>
                ))}

                <div className="text-center mt-auto">
                  <Link
                    to={getArticleCategoryUrl("unsung-heroes")}
                    className="bg-cyan-500 text-gray-900 px-4 py-2 text-sm font-semibold hover:bg-cyan-400 transition-colors inline-block"
                  >
                    VIEW ALL HEROES
                  </Link>
                </div>
              </section>

              {/* Recovery Chronicles Section */}
              <section className="md:pl-6 flex flex-col">
                <div className="border-b-2 border-cyan-500 mb-4 pb-2">
                  <h3 className="text-xl md:text-2xl font-bold text-cyan-400">
                    RECOVERY CHRONICLES
                  </h3>
                  <p className="text-sm text-gray-400 italic">
                    Personal Journeys
                  </p>
                </div>

                {recoveryStories.map((story, index) => (
                  <article key={story.id} className="mb-6">
                    <Link to={`/articles/${story.slug || story.id}`}>
                      <h4
                        className={`${
                          index === 0
                            ? "text-lg md:text-xl"
                            : "text-base md:text-lg"
                        } font-${
                          index === 0 ? "bold" : "semibold"
                        } mb-2 hover:text-cyan-400 cursor-pointer text-white`}
                      >
                        {story.title}
                      </h4>
                    </Link>
                    {index === 0 && story.imageUrl && (
                      <div className="w-full h-96 mb-3 border border-gray-600 rounded overflow-hidden">
                        <ArticleImage src={story.imageUrl} alt={story.title} />
                      </div>
                    )}

                    <p className="text-sm text-gray-300 leading-relaxed mb-2">
                      {story.excerpt.substring(0, 150)}...
                    </p>
                    <span className="text-xs text-gray-500">
                      {story.readTime}
                    </span>
                  </article>
                ))}

                <div className="text-center mt-auto">
                  <Link
                    to={getArticleCategoryUrl("stories-of-recovery")}
                    className="bg-cyan-500 text-gray-900 px-4 py-2 text-sm font-semibold hover:bg-cyan-400 transition-colors inline-block"
                  >
                    READ MORE STORIES
                  </Link>
                </div>
              </section>
            </div>

            {/* Blog Section */}
            <section className="border-t-2 border-cyan-500 pt-6">
              <div className="border-b-2 border-gray-600 mb-6 pb-2">
                <h3 className="text-2xl md:text-3xl font-bold text-white">
                  RECOVERY COMPASS BLOG
                </h3>
                <p className="text-gray-400 italic">
                  Insights, tips, and community updates
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {blogPosts.map((post, index) => (
                  <article
                    key={post.id}
                    className={`${
                      index < 2 && blogPosts.length > 2
                        ? "md:border-r border-gray-600 md:pr-4"
                        : ""
                    } ${
                      index < blogPosts.length - 1 && blogPosts.length > 1
                        ? "border-b md:border-b-0 border-gray-600 pb-4 md:pb-0 mb-4 md:mb-0"
                        : ""
                    }`}
                  >
                    <Link to={`/articles/${post.slug || post.id}`}>
                      <h4 className="text-base md:text-lg font-semibold mb-2 hover:text-cyan-400 cursor-pointer text-white">
                        {post.title}
                      </h4>
                    </Link>
                    <p className="text-sm text-gray-300 mb-2">
                      {post.excerpt.substring(0, 100)}...
                    </p>
                    <span className="text-xs text-gray-500">
                      {post.author && `By ${post.author} | `}
                      {formatDate(post.date)}
                    </span>
                  </article>
                ))}
              </div>
            </section>
          </main>

          {/* Sidebar - Right Column */}
          <aside className="lg:col-span-4 lg:border-l-2 border-gray-600 lg:pl-6 order-2 lg:order-2 border-t-2 lg:border-t-0 pt-6 lg:pt-0">
            {/* Search Bar */}
            <div className="mb-8">
              <h4 className="text-lg font-bold mb-3 border-b border-cyan-500 pb-1 text-white">
                SEARCH ARCHIVES
              </h4>
              <SearchBar />
            </div>

            {/* Recent Articles */}
            <div className="mb-8">
              <h4 className="text-lg font-bold mb-3 border-b border-gray-600 pb-1 text-white">
                RECENT ARTICLES
              </h4>
              <div className="space-y-4">
                {recentArticles.slice(0, 4).map((article) => (
                  <article
                    key={article.id}
                    className="border-b border-gray-700 pb-3"
                  >
                    <Link to={`/articles/${article.slug || article.id}`}>
                      <h5 className="font-semibold text-sm mb-1 hover:text-cyan-400 cursor-pointer text-white">
                        {article.title}
                      </h5>
                    </Link>
                    <span className="text-xs text-gray-500">
                      {formatDate(article.date)}
                    </span>
                  </article>
                ))}
              </div>
            </div>

            {/* Sponsors */}
            <div className="mb-8">
              <h4 className="text-lg font-bold mb-3 border-b border-gray-600 pb-1 text-white">
                SPONSORS
              </h4>
              <div className="space-y-4">
                <div className="border border-gray-600 p-3 text-center bg-gray-800">
                  <a
                    href="https://www.reverencehouse.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:opacity-80 transition-opacity"
                  >
                    <div className="bg-gray-700 h-36 mb-2 rounded flex items-center justify-center border border-gray-600">
                      <img
                        src="/Red RH Transparent.png"
                        alt="Reverence House"
                        className="h-24 w-auto object-contain"
                      />
                    </div>
                    <p className="text-xs font-semibold text-white">
                      Reverence House
                    </p>
                  </a>
                </div>
                <div className="border border-gray-600 p-3 text-center bg-gray-800">
                  <a
                    href="https://www.youtube.com/@BecomingBetter603"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:opacity-80 transition-opacity"
                  >
                    <div className="bg-gray-700 h-36 mb-2 rounded flex items-center justify-center border border-gray-600 overflow-hidden">
                      <img
                        src="/youtube banner.png"
                        alt="Becoming Better"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <p className="text-xs font-semibold text-white">
                      Becoming Better
                    </p>
                  </a>
                </div>
                <div className="border border-gray-600 p-3 text-center bg-gray-800">
                  <a
                    href="https://www.recoveryhouseportal.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:opacity-80 transition-opacity"
                  >
                    <div className="bg-gray-700 h-36 mb-2 rounded flex items-center justify-center border border-gray-600">
                      <img
                        src="/RecoveryHousePortalLogo.png"
                        alt="Recovery House Portal"
                        className="h-24 w-auto object-contain"
                      />
                    </div>
                    <p className="text-xs font-semibold text-white">
                      Recovery House Portal
                    </p>
                  </a>
                </div>
                <div className="border-2 border-dashed border-cyan-500 p-4 text-center bg-gray-800">
                  <h5 className="text-sm font-bold text-cyan-400 mb-2">
                    SPONSOR OPPORTUNITY
                  </h5>
                  <p className="text-xs text-gray-300 mb-3 leading-relaxed">
                    Reach out about sponsor opportunities to support our mission
                    of recovery and community wellness.
                  </p>
                  <Link
                    to="/contact"
                    className="bg-cyan-500 text-gray-900 px-3 py-1 text-xs font-semibold hover:bg-cyan-400 transition-colors inline-block"
                  >
                    LEARN MORE
                  </Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-600 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">CONTACT US</h5>
              <p className="text-sm text-gray-300">
                <br />
                Laconia, NH 03246
                <br />
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">SECTIONS</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href={getArticleCategoryUrl("unsung-heroes")}
                    className="hover:text-cyan-400"
                  >
                    Unsung Heroes
                  </a>
                </li>
                <li>
                  <a
                    href={getArticleCategoryUrl("stories-of-recovery")}
                    className="hover:text-cyan-400"
                  >
                    Recovery Stories
                  </a>
                </li>
                <li>
                  <a href="/resources" className="hover:text-cyan-400">
                    Resources
                  </a>
                </li>
                <li>
                  <a
                    href={getArticleCategoryUrl("blog-posts")}
                    className="hover:text-cyan-400"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">RESOURCES</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href={getResourceCategoryUrl("Primary SUD Providers")}
                    className="hover:text-cyan-400"
                  >
                    Treatment Centers
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl(
                      "Online Resources and Support Groups"
                    )}
                    className="hover:text-cyan-400"
                  >
                    Support Groups
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl("Crisis Intervention Services")}
                    className="hover:text-cyan-400"
                  >
                    Crisis Hotline
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl(
                      "Primary Mental Health Providers"
                    )}
                    className="hover:text-cyan-400"
                  >
                    Mental Health
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">FOLLOW US</h5>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61559004021648"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded flex items-center justify-center transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4 text-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Recovery Compass NH. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FrontPage;
